// ============================================================
// The beat log - which attacks and moves this screen has settled (plan map-display, D; design-gaps M5)
// ============================================================
// The fuzzer's oracles (engine/fuzz/beats) ask whether every attack in the
// log played a beat, and every move line walked its figure, on a screen
// drawing the display. A beat or a walk lasts a moment and leaves nothing
// behind, so the display notes here when it is up, and each one it SETTLED:
// played, or heard only after it had expired (a muted or offline screen
// catching up owes nothing). The fuzz tap reads it. Kept for the oracle's
// window. One list: an attack's id is `${struck}:${at}`, a move's `${mover}:${at}`.

import { BEAT_WINDOW_MS, type BeatLog } from '../engine/fuzz/beats';

/** A little longer than the oracle looks back: kept by time, not count. */
const KEEP_MS = BEAT_WINDOW_MS + 15_000;
let shownSince: number | null = null;
/** id -> when it was settled; a Map keeps insertion order, which is time order. */
const settled = new Map<string, number>();

/** The display's beat layer came up (a time) or went (null). */
export function noteBeatsShown(since: number | null): void {
  shownSince = since;
}

function settle(id: string, now: number): void {
  if (settled.has(id)) return;
  // Oldest first: stop at the first still in the window.
  for (const [old, at] of settled) {
    if (now - at <= KEEP_MS) break;
    settled.delete(old);
  }
  settled.set(id, now);
}

/**
 * This screen owes nothing more for it: a beat started, a walk begun (or stood at the end of, holding still), or
 * an attack or move heard of only after it had expired. One verb; the call site says which.
 */
export function noteSettled(id: string, now = Date.now()): void {
  settle(id, now);
}

/** What the oracle reads (engine/fuzz/beats `BeatLog`). */
export function readBeatLog(): BeatLog {
  return { shownSince, played: [...settled.keys()], at: Date.now() };
}
